import React, { useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DateTimePickerStartViewRenderers from "../coupon/DateTimePickerStart";
import DateTimePickerEndViewRenderers from "../coupon/DateTimePickerEnd";
import auth from "../../config/authaxios";

const CATEGORY_LABELS = {
  1: "Qwiksilver",
  2: "Khiladi Adda",
  3: "Coupon",
  4: "Rewards Store",
  5: "Hubble",
};

const TIMEZONE_SECTIONS = [
  { key: "utc", label: "UTC" },
  { key: "ist", label: "IST" },
];

const getCategoryLabel = (type) =>
  CATEGORY_LABELS[type] || `Category ${type}`;

const formatAmount = (value) =>
  Number(value || 0).toLocaleString("en-IN");

const formatDateRange = (startDate, endDate, timezoneLabel) => {
  if (!startDate || !endDate) return null;
  const format = (date) => dayjs(date).format("DD MMM YYYY, hh:mm A");
  return `${format(startDate)} — ${format(endDate)} (${timezoneLabel})`;
};

const getPublisherTotals = (publisher) =>
  (publisher?.categories || []).reduce(
    (acc, category) => ({
      qty: acc.qty + (category.total_qty || 0),
      amount: acc.amount + (category.total_amount || 0),
    }),
    { qty: 0, amount: 0 }
  );

const getCategoryWiseTotals = (data) => {
  const map = {};

  (data || []).forEach((publisher) => {
    (publisher.categories || []).forEach((category) => {
      const type = category.category_type;
      if (!map[type]) {
        map[type] = { total_qty: 0, total_amount: 0, priceMap: {} };
      }

      map[type].total_qty += category.total_qty || 0;
      map[type].total_amount += category.total_amount || 0;

      (category.price_breakdown || []).forEach((row) => {
        if (!map[type].priceMap[row.price]) {
          map[type].priceMap[row.price] = { price: row.price, qty: 0, amount: 0 };
        }
        map[type].priceMap[row.price].qty += row.qty || 0;
        map[type].priceMap[row.price].amount += row.amount || 0;
      });
    });
  });

  return Object.entries(map)
    .map(([type, value]) => ({
      category_type: Number(type),
      total_qty: value.total_qty,
      total_amount: value.total_amount,
      price_breakdown: Object.values(value.priceMap).sort((a, b) => a.price - b.price),
    }))
    .sort((a, b) => a.category_type - b.category_type);
};

const CategoryBreakdown = ({ category }) => (
  <Box>
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", sm: "center" }}
      spacing={1}
      sx={{ mb: 1 }}
    >
      <Typography variant="subtitle1" fontWeight={600}>
        {getCategoryLabel(category.category_type)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Qty: {formatAmount(category.total_qty)} | Amount: ₹
        {formatAmount(category.total_amount)}
      </Typography>
    </Stack>

    <TableContainer sx={{ mb: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#DC8449" }}>
            <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Price (₹)</TableCell>
            <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Quantity</TableCell>
            <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Amount (₹)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(category.price_breakdown || []).map((row) => (
            <TableRow key={`${category.category_type}-${row.price}`}>
              <TableCell>{formatAmount(row.price)}</TableCell>
              <TableCell>{formatAmount(row.qty)}</TableCell>
              <TableCell>{formatAmount(row.amount)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    <Divider />
  </Box>
);

const WithdrawSection = ({ sectionKey, label, sectionData, expandedPublisher, onTogglePublisher }) => {
  const data = sectionData?.data || [];
  const dateRange = formatDateRange(
    sectionData?.start_date,
    sectionData?.end_date,
    label
  );

  const categoryTotals = useMemo(() => getCategoryWiseTotals(data), [data]);

  const grandTotals = useMemo(
    () =>
      categoryTotals.reduce(
        (acc, cat) => ({
          qty: acc.qty + cat.total_qty,
          amount: acc.amount + cat.total_amount,
        }),
        { qty: 0, amount: 0 }
      ),
    [categoryTotals]
  );

  if (!data.length) {
    return (
      <Card sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
          {label}
        </Typography>
        {dateRange && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {dateRange}
          </Typography>
        )}
        <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
          No data available for {label}.
        </Typography>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="h6" color="primary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      {dateRange && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {dateRange}
        </Typography>
      )}

      <Card sx={{ p: 2, borderRadius: 2, bgcolor: "#fafafa", mb: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            All Publishers Summary ({label})
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label={`Total Qty: ${formatAmount(grandTotals.qty)}`}
              color="primary"
              variant="outlined"
              size="small"
            />
            <Chip
              label={`Total Amount: ₹${formatAmount(grandTotals.amount)}`}
              color="primary"
              size="small"
            />
          </Stack>
        </Stack>

        <Stack spacing={2}>
          {categoryTotals.map((category) => (
            <CategoryBreakdown
              key={`${sectionKey}-summary-${category.category_type}`}
              category={category}
            />
          ))}
        </Stack>
      </Card>

      <Stack spacing={1}>
        {data.map((publisher) => {
          const totals = getPublisherTotals(publisher);
          const accordionKey = `${sectionKey}-${publisher.publisher_id}`;
          const isExpanded = expandedPublisher === accordionKey;

          return (
            <Accordion
              key={accordionKey}
              expanded={isExpanded}
              onChange={() => onTogglePublisher(accordionKey)}
              sx={{ borderRadius: "8px !important", "&:before": { display: "none" } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                  sx={{ width: "100%", pr: 1 }}
                >
                  <Typography variant="subtitle1" fontWeight={600} color="primary">
                    {publisher.publisher_name}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      label={`Total Qty: ${formatAmount(totals.qty)}`}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      label={`Total Amount: ₹${formatAmount(totals.amount)}`}
                      color="primary"
                      size="small"
                    />
                  </Stack>
                </Stack>
              </AccordionSummary>

              <AccordionDetails>
                <Stack spacing={2}>
                  {(publisher.categories || []).map((category) => (
                    <CategoryBreakdown
                      key={`${accordionKey}-${category.category_type}`}
                      category={{
                        ...category,
                        price_breakdown: (category.price_breakdown || [])
                          .slice()
                          .sort((a, b) => a.price - b.price),
                      }}
                    />
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
    </Card>
  );
};

const hasAnyData = (response) =>
  TIMEZONE_SECTIONS.some(({ key }) => (response?.[key]?.data || []).length > 0);

const DailyWithdraw = () => {
  const [startTime, setStartTime] = useState();
  const [endTime, setEndTime] = useState();
  const [withdrawResponse, setWithdrawResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [expandedPublisher, setExpandedPublisher] = useState(null);

  const fetchWithdrawals = () => {
    setLoading(true);
    setHasSearched(true);
    setExpandedPublisher(null);

    const payload = {
      s_date: startTime,
      e_date: endTime,
      status: "COMPLETE",
    };

    auth
      .post(`/admin/total-withdrawals`, payload)
      .then((res) => {
        setWithdrawResponse(res.data?.response || null);
      })
      .catch((err) => {
        console.log("err", err);
        setWithdrawResponse(null);
      })
      .finally(() => setLoading(false));
  };

  const handlePublisherToggle = (accordionKey) => {
    setExpandedPublisher((prev) => (prev === accordionKey ? null : accordionKey));
  };

  const showResults = !loading && hasSearched && withdrawResponse;
  const showEmpty = showResults && !hasAnyData(withdrawResponse);

  return (
    <Box sx={{ minHeight: "50vh" }}>
      <Stack spacing={2} direction="row" sx={{ mb: 2 }} alignItems="center">
        <DateTimePickerStartViewRenderers setStartTime={setStartTime} />
        <DateTimePickerEndViewRenderers setEndTime={setEndTime} />
        {startTime && endTime && (
          <Button variant="contained" onClick={fetchWithdrawals} disabled={loading}>
            {loading ? "Searching..." : "Search"}
          </Button>
        )}
      </Stack>

      {loading && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          Loading withdrawal history...
        </Typography>
      )}

      {showEmpty && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          No withdrawal history found for the selected dates.
        </Typography>
      )}

      {showResults && !showEmpty && (
        <Stack spacing={3}>
          {TIMEZONE_SECTIONS.map(({ key, label }) => (
            <WithdrawSection
              key={key}
              sectionKey={key}
              label={label}
              sectionData={withdrawResponse[key]}
              expandedPublisher={expandedPublisher}
              onTogglePublisher={handlePublisherToggle}
            />
          ))}
        </Stack>
      )}

      {!loading && !hasSearched && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          Select start and end dates, then click Search to view withdrawal history.
        </Typography>
      )}
    </Box>
  );
};

export default DailyWithdraw;
